import { useNavigate } from 'react-router-dom';
import { MultipleOptionsTemplate } from '@templates';
import { parseZodErrors, stringFieldSchema } from '@utils';
import { useState } from 'react';
import { ZodError } from 'zod';
import { emptyServiceLocationDetails, ErrorMessages } from '@types';
import { scrollTop } from '@utils';
import {
  ProviderType,
  useEVChargerStore,
} from '../../../../hooks/useEVChargerStore';
import { RoutesList } from '../../../routes/Routes';
import ESB from '../../../../assets/ESB.png';
import ShellUbitricity from '../../../../assets/ShellUbitricity.png';
import SourceLondon from '../../../../assets/SourceLondon.png';
import { PROVIDER_OPTIONS_ERROR } from '../../../../constants/errors';

export const ProviderSelection = () => {
  const navigate = useNavigate();
  const {
    update,
    data: { provider },
  } = useEVChargerStore();
  const [selectedType, setSelectedType] = useState<ProviderType>(provider);
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);

  const title = 'Which provider is the EV charging point from?';

  const options = [
    {
      id: 'ESB',
      name: 'ESB',
      labelText: 'ESB Energy',
      description: '',
      radio: 'ESB',
      imageSrc: ESB,
      isDefault: provider === 'ESB',
    },
    {
      id: 'ShellUbitricity',
      name: 'ShellUbitricity',
      labelText: 'Shell Ubitricity',
      description: '',
      radio: 'ShellUbitricity',
      imageSrc: ShellUbitricity,
      isDefault: provider === 'ShellUbitricity',
    },
    {
      id: 'SourceLondon',
      name: 'SourceLondon',
      labelText: 'Source London',
      description: '',
      radio: 'SourceLondon',
      imageSrc: SourceLondon,
      isDefault: provider === 'SourceLondon',
    },
  ];

  const handlePrevious = () => {
    navigate(RoutesList.BEFORE_YOU_START);
    scrollTop();
  };

  const handleNext = () => {
    try {
      const validatedData = stringFieldSchema(
        'provider',
        PROVIDER_OPTIONS_ERROR.required.message,
      ).parse({
        provider: selectedType,
      });

      // Clear service location details if the selected provider changes
      if (provider !== selectedType) {
        update('serviceLocationDetails', emptyServiceLocationDetails);
        update('assetDetails', null);
      }

      update('provider', validatedData.provider as ProviderType);
      navigate(RoutesList.EV_CHARGER_UPLOAD_PHOTO);
    } catch (error) {
      const validationErrors = parseZodErrors(error as ZodError);
      setErrorMessages(
        validationErrors.map((error: ErrorMessages) => ({
          ...error,
          href: error.path,
        })),
      );
    }
  };

  const buttonNext = {
    label: 'Next',
    onClick: handleNext,
  };

  const buttonPrevious = {
    label: 'Previous',
    onClick: handlePrevious,
  };

  const onChange = (result: string) => {
    const keys = {
      'ESB Energy': 'ESB',
      'Shell Ubitricity': 'ShellUbitricity',
      'Source London': 'SourceLondon',
    };

    // Type guard
    if (result in keys) {
      setSelectedType(keys[result as keyof typeof keys] as ProviderType);
      setErrorMessages([]);
    } else {
      console.error(`Invalid provider: ${result}`);
    }
  };

  return (
    <MultipleOptionsTemplate
      title={title}
      options={options}
      buttonPrevious={buttonPrevious}
      buttonNext={buttonNext}
      onChange={onChange}
      errorPath={'provider'}
      errorMessages={errorMessages}
    />
  );
};
